import React, { useState } from 'react';
import { Mail, MessageCircle, MapPin, Clock, Send, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import emailjs from '@emailjs/browser';
import SEO from '../components/SEO';
import { BUSINESS } from '../constants';

const CONTACT_METHODS = [
  {
    icon: Mail,
    label: 'Email',
    value: 'info@theyaadshop.com',
    sub: 'We reply within 24 hours',
    href: 'mailto:info@theyaadshop.com',
  },
  {
    icon: MessageCircle,
    label: 'WhatsApp',
    value: 'Chat with us',
    sub: 'Fastest way to reach us',
    href: `https://wa.me/${BUSINESS.whatsapp}`,
  },
  {
    icon: MapPin,
    label: 'Location',
    value: 'Greater Toronto Area',
    sub: 'Ontario, Canada',
    href: '',
  },
  {
    icon: Clock,
    label: 'Business Hours',
    value: 'Mon – Fri, 9am – 6pm EST',
    sub: 'Weekend orders processed Monday',
    href: '',
  },
];

// TODO: Replace with your EmailJS credentials
const EMAILJS_SERVICE_ID = 'service_wholesale';
const EMAILJS_TEMPLATE_ID = 'template_contact';
const EMAILJS_PUBLIC_KEY = 'YOUR_PUBLIC_KEY';

const Contact: React.FC = () => {
  const [formState, setFormState] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      if (EMAILJS_PUBLIC_KEY === 'YOUR_PUBLIC_KEY') {
        // EmailJS not configured — redirect to mailto instead of silently discarding
        const mailtoUrl = `mailto:info@theyaadshop.com?subject=${encodeURIComponent(formState.subject)}&body=${encodeURIComponent(`From: ${formState.name} (${formState.email})\n\n${formState.message}`)}`;
        window.location.href = mailtoUrl;
        setIsSubmitting(false);
        return;
      }

      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, {
        from_name: formState.name,
        from_email: formState.email,
        subject: formState.subject,
        message: formState.message,
        to_email: 'info@theyaadshop.com',
      }, EMAILJS_PUBLIC_KEY);
      setSubmitted(true);
    } catch (err: any) {
      setError('Failed to send message. Please try emailing us directly at info@theyaadshop.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-brand-warm">
      <SEO
        title="Contact Us"
        description="Get in touch with The Yaad Shop. Wholesale inquiries, product requests, and order support. Email info@theyaadshop.com or WhatsApp us."
        path="/contact"
      />
      {/* Page Header */}
      <section className="bg-brand-dark">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-10 py-14">
          <div className="text-xs font-bold uppercase tracking-[2px] text-brand-red-light mb-3">Get In Touch</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-white mb-3">Contact Us</h1>
          <p className="text-base text-white/60 max-w-[560px] leading-relaxed">
            Have a question about wholesale ordering? We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="max-w-[1280px] mx-auto px-5 sm:px-10 py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
          {CONTACT_METHODS.map((method) => {
            const content = (
              <div className="bg-white rounded-2xl border border-gray-100 p-6 transition-all hover:shadow-lg hover:-translate-y-1 hover:border-brand-red h-full">
                <div className="w-[50px] h-[50px] rounded-xl bg-gradient-to-br from-brand-green/10 to-brand-green/5 flex items-center justify-center mb-4">
                  <method.icon className="w-6 h-6 text-brand-green" />
                </div>
                <div className="text-xs font-semibold uppercase tracking-wider text-brand-text-muted mb-1.5">{method.label}</div>
                <div className="font-display font-bold text-brand-dark mb-1">{method.value}</div>
                <div className="text-[13px] text-brand-text-muted">{method.sub}</div>
              </div>
            );

            return method.href ? (
              <a key={method.label} href={method.href} target="_blank" rel="noopener noreferrer" className="block">
                {content}
              </a>
            ) : (
              <div key={method.label}>{content}</div>
            );
          })}
        </div>

        {/* Contact Form */}
        <div className="grid lg:grid-cols-5 gap-12 items-start">
          <div className="lg:col-span-2">
            <div className="text-xs font-bold uppercase tracking-[2px] text-brand-green mb-3">Send a Message</div>
            <h2 className="font-display text-3xl font-extrabold text-brand-dark mb-4">We'd Love to Hear From You</h2>
            <p className="text-[15px] text-brand-text-muted leading-relaxed mb-6">
              Whether you're a new store looking to stock multicultural products, an existing partner with a question, or just want to say hello — drop us a line.
            </p>
            <div className="space-y-3 text-sm text-brand-text-muted">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-brand-green flex-shrink-0" />
                <span>Wholesale account inquiries</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-brand-green flex-shrink-0" />
                <span>Product requests and sourcing</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-brand-green flex-shrink-0" />
                <span>Order support and shipping questions</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-brand-green flex-shrink-0" />
                <span>Partnership and bulk deals</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            {submitted ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
                <div className="w-16 h-16 bg-brand-green/10 rounded-full flex items-center justify-center mx-auto mb-5">
                  <CheckCircle className="w-8 h-8 text-brand-green" />
                </div>
                <h3 className="font-display font-bold text-xl text-brand-dark mb-2">Message Sent!</h3>
                <p className="text-brand-text-muted text-sm mb-5">Thanks for reaching out. We'll get back to you within 24 hours.</p>
                <button
                  onClick={() => { setSubmitted(false); setFormState({ name: '', email: '', subject: '', message: '' }); }}
                  className="text-sm font-semibold text-brand-red hover:text-brand-red-dark transition-colors"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-100 p-8">
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2 mb-4">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-red-700 text-sm">{error}</p>
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-brand-dark mb-1.5">Your Name</label>
                    <input
                      id="contact-name"
                      type="text"
                      required
                      value={formState.name}
                      onChange={(e) => setFormState({ ...formState, name: e.target.value })}
                      placeholder="John Smith"
                      className="w-full border border-gray-200 rounded-[8px] px-4 py-3 text-sm text-brand-dark placeholder:text-brand-text-muted/50 focus:ring-2 focus:ring-brand-red focus:border-brand-red outline-none transition-all"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-brand-dark mb-1.5">Email Address</label>
                    <input
                      id="contact-email"
                      type="email"
                      required
                      value={formState.email}
                      onChange={(e) => setFormState({ ...formState, email: e.target.value })}
                      placeholder="john@yourbusiness.ca"
                      className="w-full border border-gray-200 rounded-[8px] px-4 py-3 text-sm text-brand-dark placeholder:text-brand-text-muted/50 focus:ring-2 focus:ring-brand-red focus:border-brand-red outline-none transition-all"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-brand-dark mb-1.5">Subject</label>
                  <input
                    id="contact-subject"
                    type="text"
                    required
                    value={formState.subject}
                    onChange={(e) => setFormState({ ...formState, subject: e.target.value })}
                    placeholder="Wholesale account inquiry"
                    className="w-full border border-gray-200 rounded-[8px] px-4 py-3 text-sm text-brand-dark placeholder:text-brand-text-muted/50 focus:ring-2 focus:ring-brand-red focus:border-brand-red outline-none transition-all"
                    disabled={isSubmitting}
                  />
                </div>
                <div className="mb-6">
                  <label htmlFor="contact-message" className="block text-sm font-medium text-brand-dark mb-1.5">Message</label>
                  <textarea
                    id="contact-message"
                    required
                    rows={5}
                    value={formState.message}
                    onChange={(e) => setFormState({ ...formState, message: e.target.value })}
                    placeholder="Tell us about your business and what you're looking for..."
                    className="w-full border border-gray-200 rounded-[8px] px-4 py-3 text-sm text-brand-dark placeholder:text-brand-text-muted/50 focus:ring-2 focus:ring-brand-red focus:border-brand-red outline-none transition-all resize-none"
                    disabled={isSubmitting}
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-brand-red text-white py-3.5 rounded-[10px] font-semibold text-[15px] hover:bg-brand-red-dark transition-all shadow-lg shadow-brand-red/25 hover:-translate-y-0.5 flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
